import React, { useState, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import { Icon, DivIcon } from 'leaflet'
import { Button } from './ui/Button'
import {ExternalLink, Phone} from 'lucide-react'
import 'leaflet/dist/leaflet.css'

interface MapMarker {
  id: string
  type: 'doador' | 'pedido_critico' | 'pedido_urgente' | 'pedido_normal' | 'hemocentro' | 'user'
  position: { lat: number; lng: number }
  name: string
  bloodType?: string
  city?: string
  state?: string
  distance?: number
  urgency?: string
  hospital?: string
  phone?: string
  whatsapp?: string
  hours?: string
  activeDate?: string
  createdAt?: string
}

interface LeafletMapComponentProps {
  markers: MapMarker[]
  center: { lat: number; lng: number }
  zoom: number
  onMarkerClick?: (marker: MapMarker) => void
  userLocation?: { lat: number; lng: number } | null
}

// Componente para recentrar o mapa quando a localização mudar
function ChangeView({ center, zoom }: { center: { lat: number; lng: number }; zoom: number }) {
  const map = useMap()
  useEffect(() => {
    map.setView([center.lat, center.lng], zoom)
  }, [center.lat, center.lng, zoom, map])
  return null
}

export function LeafletMapComponent({ 
  markers, 
  center, 
  zoom, 
  onMarkerClick,
  userLocation 
}: LeafletMapComponentProps) {
  const [selectedMarker, setSelectedMarker] = useState<MapMarker | null>(null)

  const getMarkerIcon = (type: string) => {
    const colors: Record<string, string> = {
      'doador': '#ef4444',
      'pedido_critico': '#991b1b',
      'pedido_urgente': '#f97316',
      'pedido_normal': '#eab308',
      'hemocentro': '#3b82f6',
      'user': '#06b6d4'
    }
    
    const emojis: Record<string, string> = {
      'doador': '❤️',
      'pedido_critico': '🚨',
      'pedido_urgente': '⚠️',
      'pedido_normal': '📌',
      'hemocentro': '🏥',
      'user': '📍'
    }

    const color = colors[type] || '#6b7280'
    const emoji = emojis[type] || '📍'
    const pulseClass = type === 'pedido_critico' ? 'animate-pulse' : ''

    return new DivIcon({
      html: `<div class="relative flex items-center justify-center ${pulseClass}">
        <div class="absolute w-8 h-8 rounded-full" style="background-color: ${color}; opacity: 0.3;"></div>
        <div class="relative text-2xl">${emoji}</div>
      </div>`,
      className: 'custom-marker',
      iconSize: [40, 40],
      iconAnchor: [20, 20],
      popupAnchor: [0, -20]
    })
  }

  const getUserIcon = () => {
    return new DivIcon({
      html: `<div class="relative flex items-center justify-center animate-pulse">
        <div class="absolute w-10 h-10 rounded-full bg-cyan-500" style="opacity: 0.4;"></div>
        <div class="absolute w-6 h-6 rounded-full bg-cyan-500" style="opacity: 0.6;"></div>
        <div class="relative w-4 h-4 rounded-full bg-cyan-600 border-2 border-white"></div>
      </div>`,
      className: 'user-location-marker',
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    })
  }

  const handleMarkerClick = (marker: MapMarker) => {
    setSelectedMarker(marker)
    if (onMarkerClick) {
      onMarkerClick(marker)
    }
  }

  return (
    <MapContainer
      center={[center.lat, center.lng]}
      zoom={zoom}
      style={{ width: '100%', height: '100%', minHeight: '500px' }}
      className="rounded-lg z-0"
    >
      <ChangeView center={center} zoom={zoom} />
      
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {/* User Location Marker */}
      {userLocation && (
        <Marker 
          position={[userLocation.lat, userLocation.lng]} 
          icon={getUserIcon()}
        >
          <Popup>
            <div className="text-center">
              <p className="font-bold text-cyan-600">📍 Você está aqui</p>
            </div>
          </Popup>
        </Marker>
      )}

      {/* Map Markers */}
      {markers.map((marker) => (
        <Marker
          key={marker.id}
          position={[marker.position.lat, marker.position.lng]}
          icon={getMarkerIcon(marker.type)}
          eventHandlers={{
            click: () => handleMarkerClick(marker)
          }}
        >
          <Popup>
            <div className="p-2 min-w-[200px]">
              <h3 className="font-bold text-gray-900 mb-2">{marker.name}</h3>
              
              {marker.bloodType && (
                <p className="text-sm text-gray-600 mb-1">
                  🩸 Tipo: <span className="font-bold">{marker.bloodType}</span>
                </p>
              )}
              
              {marker.city && marker.state && (
                <p className="text-sm text-gray-600 mb-1">
                  📍 {marker.city}/{marker.state}
                </p>
              )}
              
              {marker.distance !== undefined && marker.distance < 999 && (
                <p className="text-sm text-gray-600 mb-1">
                  📏 A {marker.distance.toFixed(1)} km de você
                </p>
              )}
              
              {marker.urgency && (
                <p className="text-sm mb-2">
                  <span className={`px-2 py-1 rounded text-xs font-bold ${
                    marker.urgency === 'critical' ? 'bg-red-100 text-red-800' :
                    marker.urgency === 'high' ? 'bg-orange-100 text-orange-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {marker.urgency === 'critical' ? '🚨 CRÍTICO' : 
                     marker.urgency === 'high' ? '⚠️ URGENTE' : '📌 NORMAL'}
                  </span>
                </p>
              )}
              
              {marker.hospital && (
                <p className="text-sm text-gray-600 mb-1">
                  🏥 {marker.hospital}
                </p>
              )}
              
              {marker.hours && (
                <p className="text-sm text-gray-600 mb-1">
                  🕐 {marker.hours}
                </p>
              )}
              
              {marker.createdAt && (
                <p className="text-xs text-gray-500 mb-2">
                  ⏰ {marker.createdAt}
                </p>
              )}
              
              <div className="flex gap-2 mt-3">
                {marker.type.includes('pedido') && (
                  <Button size="sm" className="text-xs">
                    Ver Detalhes
                  </Button>
                )}
                
                {marker.type === 'hemocentro' && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const address = `${marker.name}, ${marker.city}, ${marker.state}`
                        window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank')
                      }}
                      className="text-xs"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Mapa
                    </Button>
                    {marker.phone && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`tel:${marker.phone}`, '_self')}
                        className="text-xs"
                      >
                        <Phone className="h-3 w-3 mr-1" />
                        Ligar
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  )
}
